import React, { useEffect, useState } from 'react'
import client from '../../client/Client';
import HeaderBanner from '../ui/HeaderBanner';

type TravelType = {
  ArrivalToAirports: ArrivalToAirports[];
  RECOMMENDATIONVISARequirements: RECOMMENDATIONVISARequirements[];
  GeneralInformation: GeneralInformation[];
  GeneralInformationimage: GeneralInformationimage;
  LocalCurrency: LocalCurrency[];
};

interface ArrivalToAirports {
  _key: string;
  AirportImage?: {
    _type: string;
    asset: {
      _ref: string;
      _type: string;
    };
  };
  AirportName?: string;
  AirportDescription?: RichTextBlock[];
  _type: string;
}

interface RECOMMENDATIONVISARequirements extends RichTextBlock {}

interface GeneralInformation extends RichTextBlock {}

interface GeneralInformationimage {
  asset: {
    _type: string;
    _ref: string;
  };
  _type: string;
}

interface LocalCurrency extends RichTextBlock {
  markDefs: Link[];
  children: Span[];
}

interface Link {
  _type: string;
  href: string;
  _key: string;
}

interface Span {
  _key: string;
  _type: string;
  marks: string[];
  text: string;
}

interface RichTextBlock {
  _key: string;
  listItem?: string;
  markDefs?: any[];
  children: any[];
  level?: number;
  _type: string;
  style: string;
}

/**
 * Helper to render Sanity rich-text children with:
 * - bold / underline styles
 * - link wrapping via markDefs
 */
const renderMarkedChildren = (block: { children: any[]; markDefs?: any[] }) => {
  return block.children.map((child: any, childIdx: number) => {
    // Find link if present (same logic as original: use first mark as link key)
    const linkData =
      child.marks && child.marks.length > 0
        ? block.markDefs?.find((def: any) => def._key === child.marks[0])
        : null;

    // Detect text formatting marks
    const hasStrong = child.marks?.includes('strong');
    const hasUnderline = child.marks?.includes('underline');

    // Style rules
    let textClasses = '';
    if (hasStrong && hasUnderline) {
      textClasses = 'font-semibold text-red-600'; // bold + red
    } else if (hasStrong) {
      textClasses = 'font-semibold';
    } else if (hasUnderline) {
      textClasses = 'underline';
    }

    const content = (
      <span key={childIdx} className={textClasses}>
        {child.text}
      </span>
    );

    if (linkData) {
      return (
        <a
          key={childIdx}
          href={linkData.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-901 hover:text-blue-800 font-medium underline transition-colors duration-200"
        >
          {content}
        </a>
      );
    }

    return content;
  });
};

const Travel = () => {
  const [data, setData] = useState<TravelType>();

  useEffect(() => {
    client
      .fetch('*[_type == "AccomodationANDRegistration"]')
      .then((result) => {
        setData(result[0]);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, []);

  const getImageUrl = (ref: string) => {
    return `https://cdn.sanity.io/images/79vgbaej/production/${ref.slice(
      6,
      -4
    )}.${ref.slice(-3)}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
      <div
        className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1"
        aria-hidden="true"
      >
        <svg
          width="1360"
          height="578"
          viewBox="0 0 1360 578"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>
          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>

      <HeaderBanner title="Travel Information" />

      <div className="text-left space-y-8 pt-12">
        {/* Visa Requirements Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm">
          <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
            Visa Requirements & Recommendations
          </h1>
          <div className="space-y-3">
            {data?.RECOMMENDATIONVISARequirements &&
              data.RECOMMENDATIONVISARequirements.length > 0 &&
              data.RECOMMENDATIONVISARequirements.map((block, index) => (
                <div
                  key={block._key || index}
                  className="text-gray-800 hover:text-white bg-white border border-gray-200 p-4 rounded-md shadow-sm hover:shadow-md hover:bg-red-500 transition-all duration-300"
                >
                  <p className="leading-relaxed">{renderMarkedChildren(block)}</p>
                </div>
              ))}
          </div>
        </div>

        {/* General Information Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm">
          <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
            General Information
          </h1>
          <div className="space-y-3 mb-6">
            {data?.GeneralInformation &&
              data.GeneralInformation.length > 0 &&
              data.GeneralInformation.map((block, index) => (
                <p
                  key={block._key || index}
                  className="text-gray-700 leading-relaxed"
                >
                  {renderMarkedChildren(block)}
                </p>
              ))}
          </div>
          {data?.GeneralInformationimage && (
            <img
              className="w-full rounded-lg object-cover object-center shadow-md"
              src={getImageUrl(data.GeneralInformationimage.asset._ref)}
              alt="General travel information"
            />
          )}
        </div>

        {/* Transportation Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm">
          <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-6">
            Transportation
          </h1>
          <div className="space-y-8">
            {data?.ArrivalToAirports?.map((item) => (
              <div key={item._key} className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold text-red-500 mb-4">
                  {item.AirportName}
                </h2>
                <div className="space-y-3 mb-4">
                  {item.AirportDescription &&
                    item.AirportDescription.length > 0 &&
                    item.AirportDescription.map((description, descIndex) => (
                      <p
                        key={description._key || descIndex}
                        className="text-gray-700 leading-relaxed"
                      >
                        {renderMarkedChildren(description)}
                      </p>
                    ))}
                </div>

                {item.AirportImage && (
                  <img
                    className="w-full md:w-1/2 rounded-lg object-cover object-center shadow-sm"
                    src={getImageUrl(item.AirportImage.asset._ref)}
                    alt={`${item.AirportName} information`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Currency Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm">
          <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
            Local Currency
          </h1>
          <div className="space-y-3">
            {data?.LocalCurrency &&
              data.LocalCurrency.length > 0 &&
              data.LocalCurrency.map((block, blockIndex) => (
                <p
                  key={block._key || blockIndex}
                  className="text-gray-700 leading-relaxed"
                >
                  {renderMarkedChildren(block)}
                </p>
              ))}
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Need Assistance?
          </h3>
          <p className="text-gray-700">
            For any travel-related questions or assistance, please contact:{' '}
            <a
              className="text-red-600 hover:text-red-800 font-medium underline"
              href="mailto:info@ssd-multiconf.org"
            >
              info@ssd-multiconf.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Travel;
